import React, { useState, useEffect, useRef } from 'react';
import './Skills.css';
import { trackSectionView } from '../utils/analytics';

interface Skill {
  name: string;
  level: number; // 1-5 rating
  category: 'frontend' | 'backend' | 'cloud' | 'mobile' | 'tools';
}

const Skills: React.FC = () => {
  const [visibleCategories, setVisibleCategories] = useState<Record<string, boolean>>({});
  const skillsRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  const skills: Skill[] = [
    // Frontend Skills
    { name: 'React.js', level: 5, category: 'frontend' },
    { name: 'Angular', level: 4, category: 'frontend' },
    { name: 'TypeScript', level: 4, category: 'frontend' },
    { name: 'JavaScript', level: 5, category: 'frontend' },
    { name: 'HTML5/CSS3', level: 5, category: 'frontend' },
    { name: 'Tailwind CSS', level: 4, category: 'frontend' },
    { name: 'Redux', level: 4, category: 'frontend' },
    { name: 'Responsive Design', level: 5, category: 'frontend' },
    
    // Backend Skills
    { name: 'Python', level: 5, category: 'backend' },
    { name: 'Node.js', level: 4, category: 'backend' },
    { name: 'Flask', level: 4, category: 'backend' },
    { name: 'C#', level: 4, category: 'backend' },
    { name: 'RESTful APIs', level: 5, category: 'backend' },
    { name: 'PostgreSQL', level: 4, category: 'backend' },
    { name: 'MongoDB', level: 4, category: 'backend' },
    { name: 'DynamoDB', level: 4, category: 'backend' },

    // Cloud & DevOps
    { name: 'AWS Lambda', level: 4, category: 'cloud' },
    { name: 'AWS EC2', level: 4, category: 'cloud' },
    { name: 'AWS S3', level: 4, category: 'cloud' },
    { name: 'GCP/Firebase', level: 3, category: 'cloud' },
    { name: 'Docker', level: 3, category: 'cloud' },
    { name: 'CI/CD', level: 3, category: 'cloud' },
    
    // Mobile & Game Dev
    { name: 'Swift', level: 3, category: 'mobile' },
    { name: 'Unity', level: 3, category: 'mobile' },
    { name: 'C++', level: 3, category: 'mobile' },
    
    // Tools & Others
    { name: 'Git', level: 5, category: 'tools' },
    { name: 'Agile/Scrum', level: 4, category: 'tools' },
    { name: 'Web Scraping', level: 4, category: 'tools' },
    { name: 'NumPy/pandas', level: 4, category: 'tools' },
    { name: 'TDD', level: 3, category: 'tools' },
  ];

  const categories = [
    { id: 'frontend', label: 'Frontend Development' },
    { id: 'backend', label: 'Backend Development' },
    { id: 'cloud', label: 'Cloud & DevOps' },
    { id: 'mobile', label: 'Mobile & Game Dev' },
    { id: 'tools', label: 'Tools & Data Science' }
  ];

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    
    // Create observer for each category
    categories.forEach(category => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleCategories(prev => ({
              ...prev,
              [category.id]: true
            }));
            observer.disconnect();
          }
        },
        { 
          threshold: window.innerWidth <= 768 ? 0.1 : 0.3 // Lower threshold for mobile
        }
      );
      
      observers.push(observer);
      
      if (categoryRefs.current[category.id]) {
        observer.observe(categoryRefs.current[category.id]!);
      }
    });

    // Track section view when first category becomes visible
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackSectionView('skills');
          sectionObserver.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (skillsRef.current) {
      sectionObserver.observe(skillsRef.current);
    }

    return () => {
      observers.forEach(observer => observer.disconnect());
      sectionObserver.disconnect();
    };
  }, []);

  // Helper function to render skill dots
  const renderSkillDots = (level: number, skillIndex: number, categoryId: string) => {
    const dots = [];
    const isCategoryVisible = visibleCategories[categoryId];
    
    for (let i = 1; i <= 5; i++) {
      const shouldFill = i <= level;
      const animationDelay = isCategoryVisible ? (skillIndex * 0.1) + (i * 0.15) : 0;
      
      dots.push(
        <span 
          key={i} 
          className={`skill-dot ${shouldFill ? 'filled' : 'empty'} ${isCategoryVisible ? 'animate' : ''}`}
          style={{ 
            animationDelay: shouldFill ? `${animationDelay}s` : '0s'
          }}
          aria-hidden="true"
        ></span>
      );
    }
    return dots;
  };

  return (
    <section id="skills" className="skills">
      <div className="section-container">
        <h2 className="section-title">My Skills</h2>
        <p className="section-subtitle">Here's my technical skill set and expertise.</p>
        
        <div className="skills-categories" ref={skillsRef}>
          {categories.map(category => {
            let categorySkillIndex = 0;
            return (
              <div 
                key={category.id} 
                className="skills-category"
                ref={el => categoryRefs.current[category.id] = el}
              >
                <h3>{category.label}</h3>
                <div className="skills-list">
                  {skills
                    .filter(skill => skill.category === category.id)
                    .sort((a, b) => b.level - a.level)
                    .map((skill, index) => {
                      const skillAnimationIndex = categorySkillIndex++;
                      return (
                        <div key={index} className="skill-item">
                          <div className="skill-info">
                            <span className="skill-name">{skill.name}</span>
                            <div className="skill-dots">
                              {renderSkillDots(skill.level, skillAnimationIndex, category.id)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;